'use client'

/**
 * <Alert> — the canonical Crucible notification primitive.
 *
 * Wraps any inline notification, banner, or status surface with:
 *   1. Automatic `notification_shown` telemetry on mount (via
 *      useNotificationVisibility — fires exactly once per appearance)
 *   2. Click Analytics attribution via `data-cap-surface` / `data-cap-kind`
 *      attributes on the root element (clicks on any descendant button
 *      bubble up to AppInsights as `Click` customEvents tagged with the
 *      surface name).
 *   3. Motion: enter ease-out 220ms (translateY 8→0 + opacity 0→1),
 *      exit ease-in 160ms (translateY 0→4 + opacity 1→0). Honored across
 *      surface kinds — color/icon differentiate, motion stays uniform.
 *   4. prefers-reduced-motion: drops translate, keeps opacity fade over
 *      120ms (reduced ≠ removed).
 *
 * New persona surfaces get telemetry for free by using this primitive.
 * Bespoke surfaces that can't restructure their markup should call
 * `useNotificationVisibility` directly from lib/telemetry.
 *
 * AB#19124 slice 4.
 */

import { forwardRef, type ReactNode } from 'react'
import { AnimatePresence, motion, useReducedMotion, type HTMLMotionProps } from 'motion/react'
import { cn } from '@/lib/utils'
import { useNotificationVisibility, type NotificationKind } from '@/lib/telemetry'

// Use motion's HTMLMotionProps so onDrag/onAnimationStart/etc carry the
// motion-compatible signatures rather than React's stock DragEventHandler
// (which conflicts with motion's PanInfo-typed handler).
type AlertHTMLProps = Omit<HTMLMotionProps<'div'>, 'children' | 'ref'>

export interface AlertProps extends AlertHTMLProps {
  /**
   * Identifies the notification source in AppInsights customEvents.
   * Convention: kebab-case, descriptive of the component. Examples:
   *   'session-guard', 'version-guard', 'maintenance-banner',
   *   'route-handoff', 'audrey-consistency-check', 'norma-import-result'.
   */
  surface: string
  /** Severity / actionability of the message. */
  kind: NotificationKind
  /** Controls visibility. Toggling false→true fires telemetry once. */
  visible?: boolean
  /** Extra dimensions sent with `notification_shown` (e.g. reason). */
  telemetryProps?: Record<string, string>
  children: ReactNode
}

const ENTER = { duration: 0.22, ease: [0, 0, 0.2, 1] as const } // ease-out
const EXIT = { duration: 0.16, ease: [0.4, 0, 1, 1] as const } // ease-in
const REDUCED = { duration: 0.12, ease: 'linear' as const }

/**
 * <NotificationOnMount> — client-side telemetry beacon for surfaces that
 * can't host a hook directly (e.g. server components like the
 * MaintenanceBanner). Renders nothing; fires `notification_shown` once
 * when this component mounts.
 *
 * Usage: drop inside a server-rendered conditional block. Mounting only
 * happens when the parent renders the block, so this acts as a
 * server-rendered "I am visible" beacon.
 */
export function NotificationOnMount({
  surface,
  kind,
  telemetryProps,
}: {
  surface: string
  kind: NotificationKind
  telemetryProps?: Record<string, string>
}) {
  useNotificationVisibility(surface, kind, true, telemetryProps)
  return null
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { surface, kind, visible = true, telemetryProps, className, children, ...rest },
  ref,
) {
  useNotificationVisibility(surface, kind, visible, telemetryProps)
  const reduced = useReducedMotion()

  const motionProps = reduced
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: REDUCED,
      }
    : {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0, transition: ENTER },
        exit: { opacity: 0, y: 4, transition: EXIT },
      }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          ref={ref}
          role={kind === 'error' || kind === 'action-required' ? 'alert' : 'status'}
          aria-live={kind === 'error' || kind === 'action-required' ? 'assertive' : 'polite'}
          data-cap-surface={surface}
          data-cap-kind={kind}
          className={cn(className)}
          {...motionProps}
          {...rest}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  )
})
